import AjaxService, { validateResponse } from '../services/ajax-service';

import { handleServerError } from 'shared-services/error-service';
import { convertToFormData } from '../utils/helpers';
import { normalizeDateForServer } from '../utils/date';

export const fetchUsers = ({ limit, offset }) => {
    return AjaxService({
        method: 'get',
        url: `members`,
        params: {
            limit,
            offset
        }
    })
        .then(response => validateResponse(response, 'items').data)
        .catch(handleServerError);
}

export const fetchUser = userId => {
    return AjaxService({
        method: 'get',
        url: `members/${userId}`
    })
        .then(response => response?.data)
        .catch(error => {
            handleServerError(error, {
                silent: true
            });
        });
}

export const fetchCurrentUser = (masterUser, token) => {
    return AjaxService({
        method: 'get',
        url: 'users/me',
        params: {
            type: masterUser ? 'master' : 'team'
        }
    }, masterUser, token)
        .then(response => ({
            ...response.data,
            self: true
        }))
        .catch(handleServerError);
}

export const fetchUserBasicInfo = token => {
    return AjaxService({
        method: 'get',
        url: 'api/members/basicinfo'
    }, false, token)
        .then(response => response?.data);
}

export const fetchSubscriptions = () => {
    return AjaxService({
        method: 'get',
        url: 'users/me/subscriptions'
    })
        .then(response => response?.data)
        .catch(handleServerError);
}

export const updateCurrentUser = ({ tz, tz_offset, tz_iana, theme, name, avatar, email, password, trello_token, team_id, tour_passed, help_skipped, date_format, time_format, week_start, language, notificationSettings }) => {
    const data = {
        tz: tz ? tz.toString() : tz,
        tz_offset: tz_offset ? tz_offset.toString() : tz_offset,
        tz_iana: tz_iana ? tz_iana.toString() : tz_iana,
        theme,
        name,
        avatar,
        email,
        password,
        trello_token,
        team_id,
        tour_passed,
        help_skipped,
        date_format,
        time_format,
        week_start,
        language,
        notificationSettings
    };

    return AjaxService({
        method: 'put',
        url: 'users/me',
        data: data.avatar ? convertToFormData(data) : data
    }, true)
        .then(response => response?.data)
        .catch(handleServerError);
}

export const updateCurrentUserPassword = ({ new_password, old_password }) => {
    return AjaxService({
        method: 'put',
        url: 'users/me/password',
        data: {
            new_password,
            old_password
        }
    }, true)
        .then(response => response?.data)
        .catch(handleServerError);
}

export const updateCurrentUserEmail = ({ email, password }) => {
    return AjaxService({
        method: 'put',
        url: 'users/me/email',
        data: {
            email,
            password
        }
    }, true)
        .then(response => response?.data)
        .catch(handleServerError);
}

export const updateCurrentUserStatus = available => {
    return AjaxService({
        method: 'put',
        url: 'users/me/available',
        data: { available }
    })
        .then(response => response?.data)
        .catch(handleServerError);
}

export const getCurrentUserEvents = ({ from, limit, offset }) => {
    return AjaxService({
        method: 'get',
        url: 'users/me/events',
        params: {
            from: normalizeDateForServer(from),
            limit,
            offset
        }
    })
        .then(response => response?.data)
        .catch(error => {
            handleServerError(error, {
                silent: true
            });
        });
}

export const unlinkSSO = provider => {
    return AjaxService({
        method: 'delete',
        url: 'users/social',
        params: { provider }
    }).then(response => response?.data);
}

export const setNotificationSetting = ({ type, action, transport }) => {
    return AjaxService({
        method: 'put',
        url: 'users/me/settings',
        data: {
            type,
            action,
            transport
        }
    })
        .then(response => response?.data)
        .catch(handleServerError);
}

export const removeNotificationSetting = ({ type, action, transport }) => {
    return AjaxService({
        method: 'delete',
        url: 'users/me/settings',
        data: {
            type,
            action,
            transport
        }
    })
        .then(response => response?.data)
        .catch(handleServerError);
}

export const updateUser = (userId, { role_id } = {}) => {
    return AjaxService({
        method: 'put',
        url: `members/${userId}`,
        data: { role: role_id }
    })
        .then(response => response?.data)
        .catch(handleServerError);
}

export const resendInvite = (userId, projectId) => {
    return AjaxService({
        method: 'post',
        url: `members/${userId}`,
        params: {
            project_id: projectId
        }
    })
        .then(response => response?.data)
        .catch(handleServerError);
}

export const addUserLabels = (userId, labels, configOnly) => {
    const requestConfig = {
        method: 'put',
        url: `members/${userId}/labels`,
        data: { labels }
    };

    if (configOnly) {
        return requestConfig;
    }

    return AjaxService(requestConfig)
        .then(response => response?.data)
        .catch(handleServerError);
}

export const removeUserLabels = (userId, labels, configOnly) => {
    const requestConfig = {
        method: 'delete',
        url: `members/${userId}/labels`,
        data: { labels }
    };

    if (configOnly) {
        return requestConfig;
    }

    return AjaxService(requestConfig)
        .then(response => response?.data)
        .catch(handleServerError);
}

export const removeUser = (userId, projectId, projectsOnly) => {
    return AjaxService({
        method: 'delete',
        url: `members/${userId}`,
        params: {
            project_id: projectId,
            only_projects: projectsOnly
        }
    })
        .then(response => response?.data)
        .catch(handleServerError);
}

export const getSSOProviders = email => {
    return AjaxService({
        method: 'get',
        url: `users/sso_providers/${email}`
    })
        .then(response => response?.data?.sso_providers)
        .catch(handleServerError);
}

export const fetchMembersStoryPoints = ({ from, to, limit, offset }) => {
    return AjaxService({
        method: 'get',
        url: `members/story_points`,
        params: {
            from: normalizeDateForServer(from),
            to: normalizeDateForServer(to),
        }
    })
        .then(response => response?.data)
        .catch(handleServerError);
}
